import cn from 'classnames';
import { LinkIcon } from '@/assets/icons/LinkIcon';
import { IconSignOut } from '@/assets/icons/IconSignOut';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Link } from '@/ui-components/atoms/Link/Link';
import type { DownloadButtonProps } from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { SnapshotDivider } from '@/ui-components/organisms/Snapshot/SnapshotDivider/SnapshotDivider';
import { SnapshotAppFooter } from '@/ui-components/organisms/Snapshot/SnapshotAppFooter/SnapshotAppFooter';
import styles from './signedInMenu.module.scss';

export interface SignedInMenuAction {
    label: string;
    href?: string;
    ariaLabel?: string;
    target?: string;
    onSelect?: () => void | Promise<void>;
    icon?: 'external' | 'signout';
    prominence?: 'primary' | 'secondary';
    dataLinkname?: string;
}

export interface SignedInMenuProps {
    userName: string;
    accountNumber: string;
    primaryAction: SignedInMenuAction;
    accountActions: SignedInMenuAction[];
    signOutAction: SignedInMenuAction;
    appDownloadItems: DownloadButtonProps[];
}

function ActionIcon({ icon }: { icon?: SignedInMenuAction['icon'] }): React.JSX.Element | null {
    if (icon === 'external') {
        return <LinkIcon />;
    }

    if (icon === 'signout') {
        return <IconSignOut />;
    }

    return null;
}

function SignedInMenuActionRow({ action }: { action: SignedInMenuAction }): React.JSX.Element {
    const prominence = action.prominence || 'secondary';
    const hasIcon = Boolean(action.icon);
    const rowCn = cn(styles.actionRow, {
        [styles.actionRowPrimary]: prominence === 'primary',
    });
    const labelCn = cn(styles.actionLabel, {
        [styles.actionLabelPrimary]: prominence === 'primary',
        [styles.actionLabelSecondary]: prominence === 'secondary',
    });
    const content = (
        <>
            <span className={labelCn}>{action.label}</span>
            {hasIcon && (
                <span
                    className={cn(styles.actionIcon, {
                        [styles.actionIconSignout]: action.icon === 'signout',
                    })}
                >
                    <ActionIcon icon={action.icon} />
                </span>
            )}
        </>
    );

    if (!action.href) {
        return (
            <button
                type="button"
                className={rowCn}
                aria-label={action.ariaLabel}
                data-linkname={action.dataLinkname}
                onClick={() => {
                    void action.onSelect?.();
                }}
            >
                {content}
            </button>
        );
    }

    return (
        <Link
            href={action.href}
            className={rowCn}
            aria-label={action.ariaLabel}
            target={action.target}
            data-linkname={action.dataLinkname}
            onClick={() => {
                void action.onSelect?.();
            }}
        >
            {content}
        </Link>
    );
}

export function SignedInMenu({
    userName,
    accountNumber,
    primaryAction,
    accountActions,
    signOutAction,
    appDownloadItems,
}: SignedInMenuProps): React.JSX.Element {
    return (
        <div className={styles.menu}>
            <header className={styles.header}>
                <Text tag="h6" variant="h6" className={styles.headerName}>
                    {userName}
                </Text>
                {accountNumber && (
                    <Text tag="p" variant="text-medium" className={styles.headerAccount}>
                        {`Account: ${accountNumber}`}
                    </Text>
                )}
            </header>

            <div className={styles.sections}>
                <SnapshotDivider />
                <section className={styles.section}>
                    <SignedInMenuActionRow action={{ ...primaryAction, prominence: 'primary' }} />
                    {accountActions.map((action) => (
                        <SignedInMenuActionRow key={action.label} action={{ ...action, prominence: 'secondary' }} />
                    ))}
                </section>
                <SnapshotDivider />
                <section className={styles.section}>
                    <SignedInMenuActionRow action={{ ...signOutAction, icon: 'signout', prominence: 'primary' }} />
                </section>
                <SnapshotAppFooter items={appDownloadItems} />
            </div>
        </div>
    );
}
